'use client';

import { useRef, useState, type FC, type MouseEvent } from 'react';
import { m, useReducedMotion } from 'motion/react';

import type { TAuctionBoardModel, TBoardLaneKind } from '@/utils/auctions/auctionBoard';
import { Heading } from '@/components/ui/heading/heading';
import { fadeSlideUp, staggerContainer } from '@/utils/animation/variants';
import { gtag } from '@/helpers/gtag';

import { AuctionBoardArrows } from './auction-board-arrows';
import { AuctionBoardRail } from './auction-board-rail';
import { AuctionBoardRow } from './auction-board-row';
import { AuctionBoardSwitch, lanePanelId, laneTabId } from './auction-board-switch';
import { usePageScroll } from './use-page-scroll';

/**
 * The homepage auction board (spec 014).
 *
 * Three things in the order a visitor reads them: proof (what sold, for how
 * much), the offer (what is coming up), the way in (which models to look at).
 * The server decides what exists — this only lays it out. Every lane it is
 * given is rendered into the page; the one not selected is `hidden`, so the
 * content is crawlable and the switch is instant (research R3).
 *
 * The header's right side holds the lane switch and the arrows on ONE line:
 * the arrows page whichever lane is selected. Each lane keeps its own scroll
 * position, so flipping back lands where the visitor left off.
 *
 * Copy is fixed here, not read from the CMS. Nothing here says who bought a
 * car (FR-022).
 */

const HEADING_ID = 'tablica-aukcji';

const heading = (model: TAuctionBoardModel): { eyebrow: string; text: string; highlight: string } => {
  if (model.lanes.length === 2) {
    return { eyebrow: 'Copart i IAAI', text: 'Co dzieje się na aukcjach', highlight: 'na aukcjach' };
  }

  const lane = model.lanes[0];

  if (lane?.kind === 'sold') return { eyebrow: 'Za ile poszły?', text: lane.label, highlight: 'wylicytowane' };
  if (lane?.kind === 'upcoming') return { eyebrow: 'Co przed nami?', text: lane.label, highlight: 'licytacje' };

  return { eyebrow: 'Copart i IAAI', text: 'Aukcje w USA i Kanadzie', highlight: 'USA i Kanadzie' };
};

const track = (event: MouseEvent<HTMLElement>) => {
  const link = (event.target as HTMLElement).closest<HTMLAnchorElement>('a[data-lane]');

  if (!link) return;

  gtag('event', 'select_content', {
    content_type: 'auction_board',
    lane: link.dataset.lane,
    item_id: link.dataset.id,
  });
};

export const AuctionBoard: FC<{ model: TAuctionBoardModel }> = ({ model }) => {
  const prefersReducedMotion = useReducedMotion();
  const [selected, setSelected] = useState<TBoardLaneKind | null>(model.lanes[0]?.kind ?? null);

  // One ref and one pager per lane kind, always both, so the hook count is
  // constant whatever the server sent.
  const soldRef = useRef<HTMLDivElement>(null);
  const upcomingRef = useRef<HTMLDivElement>(null);
  const pagers = { sold: usePageScroll(soldRef), upcoming: usePageScroll(upcomingRef) };
  const refs = { sold: soldRef, upcoming: upcomingRef };

  const copy = heading(model);
  const hasSwitch = model.lanes.length === 2;
  const selectedLane = model.lanes.find(lane => lane.kind === selected) ?? null;

  return (
    <m.section
      data-board=""
      aria-labelledby={HEADING_ID}
      onClick={track}
      initial={prefersReducedMotion ? false : 'hidden'}
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
      variants={staggerContainer}
    >
      <m.div variants={fadeSlideUp} className="min-w-0">
        <div className="mb-2 inline-block font-oswald text-xs uppercase tracking-[0.35em] text-neutral-400">
          <span className="font-oswald">{copy.eyebrow}</span>
          <div className="mt-2 flex w-3/4 items-center gap-2">
            <div className="h-1 basis-1/3 rounded-full bg-action" />
            <div className="h-[2px] basis-2/3 rounded-full bg-neutral-700/70" />
          </div>
        </div>

        <Heading variant="h2" id={HEADING_ID} highlight={copy.highlight}>
          {copy.text}
        </Heading>
      </m.div>

      {/* The control bar: choose a lane where the row starts, page it where the
          row ends. Its own line under the title, so neither crowds the other. */}
      {selectedLane && (
        <m.div variants={fadeSlideUp} className="mb-4 mt-6 flex flex-wrap items-center justify-between gap-3">
          {hasSwitch && selected && (
            <AuctionBoardSwitch lanes={model.lanes} selected={selected} onSelect={setSelected} />
          )}
          <AuctionBoardArrows
            label={selectedLane.label}
            atStart={pagers[selectedLane.kind].atStart}
            atEnd={pagers[selectedLane.kind].atEnd}
            onPrev={pagers[selectedLane.kind].prev}
            onNext={pagers[selectedLane.kind].next}
          />
        </m.div>
      )}

      {model.lanes.map((lane, index) => (
        <m.div
          key={lane.kind}
          variants={fadeSlideUp}
          role="tabpanel"
          id={lanePanelId(lane.kind)}
          data-lane={lane.kind}
          aria-labelledby={hasSwitch ? laneTabId(lane.kind) : HEADING_ID}
          hidden={lane.kind !== selected}
        >
          <AuctionBoardRow lane={lane} scrollRef={refs[lane.kind]} priorityFirst={index === 0} />
        </m.div>
      ))}

      <m.div variants={fadeSlideUp}>
        <AuctionBoardRail links={model.rail} />
      </m.div>
    </m.section>
  );
};
